import { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ExpeditionModuleSettingsDTO } from '@api/ringil-api.v2.tsx';

export interface PrefillOptions {
  routeAndParameters: boolean;
  cargoAndOrders: boolean;
  labels: boolean;
  internalInfo: boolean;
  documents: boolean;
  carrierSelection: boolean;
  carrierNote: boolean;
  linkToSN?: boolean;
  copySNAttachments?: boolean;
  markAsPrivate?: boolean;
  /** RD-2977: `folders.required` of the expedition module settings */
  folderRequired?: boolean;
  /** RD-2977: `folders.restrictVisibility` of the expedition module settings */
  folderRestricted?: boolean;
}

export const mapModuleSettingsToPrefillOptions = (dto: ExpeditionModuleSettingsDTO): PrefillOptions => {
  const p = dto?.createShipmentPrefill;
  return {
    routeAndParameters: p?.routeAndTransport ?? true,
    cargoAndOrders: p?.cargoAndOrders ?? true,
    labels: p?.labels ?? true,
    internalInfo: p?.internalInfo ?? true,
    documents: p?.documents ?? true,
    carrierSelection: p?.carrierChoice ?? true,
    carrierNote: p?.carrierNote ?? true,
    linkToSN: p?.sn?.linkToSn ?? false,
    copySNAttachments: p?.sn?.copySnAttachments ?? false,
    markAsPrivate: p?.sn?.markAsPrivate ?? false,
    folderRequired: dto?.folders?.required ?? false,
    folderRestricted: dto?.folders?.restrictVisibility ?? false,
  };
};

/**
 * Module settings barely ever change within a session, and the components asking for them are
 * remounted often — the favourites sidebar is rebuilt on every render of its host page. Caching the
 * answer per company (and sharing a request that is already in flight) keeps that to one call.
 */
const settingsByCompanyId = new Map<string, PrefillOptions>();
const requestsInFlight = new Map<string, Promise<PrefillOptions | undefined>>();

/**
 * @param refresh RD-2977: the create-shipment page needs the current `folders.required` switch, so it refetches
 *   once per mount (the cached value is still used as the initial state) instead of trusting the session cache.
 */
export const useExpeditionPrefillDefaults = ({ refresh = false }: { refresh?: boolean } = {}): PrefillOptions | undefined => {
  const { getExpeditionModuleSettings_1 } = useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const [defaults, setDefaults] = useState<PrefillOptions>(() => settingsByCompanyId.get(loggedUser?.companyId));

  useEffect(() => {
    const companyId = loggedUser?.companyId;
    if (!companyId) return;

    const cached = settingsByCompanyId.get(companyId);
    if (cached) {
      setDefaults(cached);
      if (!refresh) return;
    }

    let request = requestsInFlight.get(companyId);
    if (!request) {
      request = new Promise<PrefillOptions | undefined>(resolve => {
        getExpeditionModuleSettings_1(
          companyId,
          d => {
            const mapped = mapModuleSettingsToPrefillOptions(d);
            settingsByCompanyId.set(companyId, mapped);
            resolve(mapped);
          },
          null,
          // Nothing is cached on failure, so the next mount retries.
          { onError: () => resolve(undefined) },
        );
      }).finally(() => requestsInFlight.delete(companyId));
      requestsInFlight.set(companyId, request);
    }

    let cancelled = false;
    request.then(value => {
      if (!cancelled && value) setDefaults(value);
    });
    return () => {
      cancelled = true;
    };
  }, [loggedUser?.companyId]);

  return defaults;
};
